import { fetchAuth } from "../../../common/api.jsx";
import { encodeId } from "../../../common/hashid.js";
import {
  WEEKLY_RECAP_J_DAYS,
  WEEKLY_RECAP_REFERENCE_METRICS,
  WEEKLY_RECAP_INTERNAL_METRICS,
  createDefaultPercentageMatrix,
  normalizePercentageMatrix,
  normalizeMicrocycleConfig,
  toFiniteNumber,
  WEEKLY_RECAP_REQUEST_TIMEOUT_MS,
  filterWeeklyRecapLoadMetrics,
} from "./weeklyRecapConfig";
import {
  buildMaquetteGlobalPlan,
  buildMaquettePlayerBundle,
  isMaquetteClub,
} from "./weeklyRecapMaquetteMock";

const weeklyRecapFetchOptions = (extra = {}) => ({
  timeoutMs: WEEKLY_RECAP_REQUEST_TIMEOUT_MS,
  ...extra,
});

function toNullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function metricListToKeyMap(rawList) {
  if (!Array.isArray(rawList)) return rawList;
  return rawList.reduce((acc, row) => {
    const key = row?.metric_key || row?.metricKey || row?.key;
    if (!key) return acc;
    acc[key] = row?.values || row?.by_day || row?.days || row;
    return acc;
  }, {});
}

function normalizeDayMetricMatrix(rawMatrix, metrics, jDays) {
  const matrix = metricListToKeyMap(rawMatrix) || {};

  return Object.fromEntries(
    metrics.map((metric) => {
      const fromRaw = matrix?.[metric.key] || {};
      const byDay = Object.fromEntries(
        jDays.map((day) => [
          day,
          toNullableNumber(fromRaw?.[day]),
        ]),
      );
      return [metric.key, byDay];
    }),
  );
}

function extractOverrideMap(rawOverrides, metrics) {
  const allowed = new Set((metrics || []).map((metric) => metric.key).filter(Boolean));
  const fromApi = metricListToKeyMap(rawOverrides) || (rawOverrides && typeof rawOverrides === "object" ? rawOverrides : {});
  const out = {};
  Object.entries(fromApi).forEach(([key, value]) => {
    if (!allowed.has(key)) return;
    const numeric = toNullableNumber(typeof value === "object" ? value?.value : value);
    if (numeric === null) return;
    out[key] = numeric;
  });
  return out;
}

function extractComputedReferenceValues(rawComputed, metrics) {
  const fromApi = metricListToKeyMap(rawComputed) || {};
  return Object.fromEntries(
    metrics.map((metric) => {
      const apiCandidate = fromApi?.[metric.key];
      const fromApiScalar = toNullableNumber(
        typeof apiCandidate === "object" ? apiCandidate?.value : apiCandidate,
      );
      return [metric.key, fromApiScalar];
    }),
  );
}

function extractReferenceValues(player, rawReferenceValues, metrics) {
  const fromApi = metricListToKeyMap(rawReferenceValues) || {};

  return Object.fromEntries(
    metrics.map((metric) => {
      const fromPlayer = toNullableNumber(metric.getValue?.(player));
      const apiCandidate = fromApi?.[metric.key];
      const fromApiScalar = toNullableNumber(
        typeof apiCandidate === "object" ? apiCandidate?.value : apiCandidate,
      );
      return [metric.key, fromApiScalar ?? fromPlayer];
    }),
  );
}

export function buildPlannedMatrix({ referenceValues, planPercentages, metrics, jDays }) {
  return Object.fromEntries(
    metrics.map((metric) => {
      const reference = toNullableNumber(referenceValues?.[metric.key]);
      const valuesByDay = Object.fromEntries(
        jDays.map((day) => {
          const pct = toFiniteNumber(planPercentages?.[metric.key]?.[day], 0);
          const value = reference === null ? null : (reference * pct) / 100;
          return [day, value];
        }),
      );
      return [metric.key, valuesByDay];
    }),
  );
}

export function computeSimilarityPercent(plannedTotal, actualTotal) {
  const p = toNullableNumber(plannedTotal);
  const a = toNullableNumber(actualTotal);

  if (p === null || a === null) return null;

  const plannedMagnitude = Math.abs(p);
  const actualMagnitude = Math.abs(a);

  if (plannedMagnitude === 0 && actualMagnitude === 0) return null;
  if (plannedMagnitude === 0 || actualMagnitude === 0) return 0;

  const high = Math.max(plannedMagnitude, actualMagnitude);
  const low = Math.min(plannedMagnitude, actualMagnitude);
  return Math.max(0, Math.min(100, (low / high) * 100));
}

export function computeAverageSimilarity(values) {
  const numeric = (values || [])
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!numeric.length) return null;
  const total = numeric.reduce((acc, value) => acc + value, 0);
  return total / numeric.length;
}

export function computeMetricTotals(matrix, metrics, jDays) {
  return Object.fromEntries(
    metrics.map((metric) => {
      let hasValue = false;
      const total = jDays.reduce((acc, day) => {
        const current = toNullableNumber(matrix?.[metric.key]?.[day]);
        if (current === null) return acc;
        hasValue = true;
        return acc + current;
      }, 0);

      if (!hasValue) {
        return [metric.key, null];
      }

      return [metric.key, total];
    }),
  );
}

function computeSimilarityByMetric({ plannedMatrix, actualMatrix, metrics, jDays }) {
  const plannedTotals = computeMetricTotals(plannedMatrix, metrics, jDays);
  const actualTotals = computeMetricTotals(actualMatrix, metrics, jDays);

  return Object.fromEntries(
    metrics.map((metric) => {
      const plannedTotal = plannedTotals?.[metric.key];
      const actualTotal = actualTotals?.[metric.key];
      return [metric.key, computeSimilarityPercent(plannedTotal, actualTotal)];
    }),
  );
}

function computeWeeklySimilarity({ plannedMatrix, actualMatrix, metrics, jDays }) {
  const perMetric = computeSimilarityByMetric({ plannedMatrix, actualMatrix, metrics, jDays });
  return computeAverageSimilarity(metrics.map((metric) => perMetric?.[metric.key]));
}

export function computeWeeklySimilarityForDays({
  plannedMatrix,
  actualMatrix,
  metrics,
  jDays,
}) {
  if (!Array.isArray(jDays) || !jDays.length) return null;

  const sumByMetricAndDays = (matrix, metricKey) => {
    let hasValue = false;
    const total = jDays.reduce((sum, day) => {
      const raw = toNullableNumber(matrix?.[metricKey]?.[day]);
      if (raw === null) return sum;
      hasValue = true;
      return sum + raw;
    }, 0);
    return hasValue ? total : null;
  };

  const metricScores = (metrics || []).map((metric) => {
    const plannedTotal = sumByMetricAndDays(plannedMatrix, metric.key);
    const actualTotal = sumByMetricAndDays(actualMatrix, metric.key);
    return computeSimilarityPercent(plannedTotal, actualTotal);
  });

  return computeAverageSimilarity(metricScores);
}

function buildBundle({
  player,
  metrics,
  jDays,
  planPercentages,
  referenceValues,
  actualValues,
  displayConfig,
  microcycle,
  calculateSimilarityFromDay,
  source,
  endpoint,
  sourceDetails,
  similarityByMetricRaw,
  weeklySimilarityRaw,
  externalWeeklySimilarityRaw,
  internalWeeklySimilarityRaw,
  targetMatchDate,
  targetMatchOpponent,
  availableTargetMatches,
  referenceMetricSources,
  referenceScope,
  referenceMatchDate,
  similarityHistory,
  dataCollection,
  recapStatus,
  loadError,
  vmaMaxByDay,
  computedReferenceValues,
  referenceOverrides,
}) {
  const plannedValues = buildPlannedMatrix({
    referenceValues,
    planPercentages,
    metrics,
    jDays,
  });

  const actualMatrix = actualValues && typeof actualValues === "object" ? actualValues : null;

  const computedSimilarityByMetric = computeSimilarityByMetric({
    plannedMatrix: plannedValues,
    actualMatrix: actualMatrix || {},
    metrics,
    jDays,
  });
  const similarityByMetric =
    similarityByMetricRaw && typeof similarityByMetricRaw === "object"
      ? similarityByMetricRaw
      : computedSimilarityByMetric;

  const loadMetrics = filterWeeklyRecapLoadMetrics(metrics);
  const computedWeeklySimilarity = computeWeeklySimilarity({
    plannedMatrix: plannedValues,
    actualMatrix,
    metrics: loadMetrics,
    jDays,
  });
  const computedExternalWeeklySimilarity = computeAverageSimilarity(
    filterWeeklyRecapLoadMetrics(WEEKLY_RECAP_REFERENCE_METRICS).map((metric) => similarityByMetric?.[metric.key]),
  );
  const computedInternalWeeklySimilarity = computeAverageSimilarity(
    WEEKLY_RECAP_INTERNAL_METRICS.map((metric) => similarityByMetric?.[metric.key]),
  );

  const externalWeeklySimilarity = toNullableNumber(externalWeeklySimilarityRaw) !== null
    ? Number(externalWeeklySimilarityRaw)
    : computedExternalWeeklySimilarity;
  const internalWeeklySimilarity = toNullableNumber(internalWeeklySimilarityRaw) !== null
    ? Number(internalWeeklySimilarityRaw)
    : computedInternalWeeklySimilarity;

  const weeklySimilarity = toNullableNumber(weeklySimilarityRaw) !== null
    ? Number(weeklySimilarityRaw)
    : (toNullableNumber(externalWeeklySimilarity) !== null ? externalWeeklySimilarity : computedWeeklySimilarity);

  return {
    playerId: player?.id,
    fetchedAt: new Date().toISOString(),
    source,
    sourceDetails,
    endpoint,
    jDays,
    referenceValues,
    planPercentages,
    plannedValues,
    actualValues: actualMatrix,
    displayConfig: displayConfig || null,
    microcycle: microcycle || null,
    calculateSimilarityFromDay: calculateSimilarityFromDay || null,
    similarityByMetric,
    weeklySimilarity,
    externalWeeklySimilarity,
    internalWeeklySimilarity,
    targetMatchDate: targetMatchDate || null,
    targetMatchOpponent: targetMatchOpponent || null,
    availableTargetMatches: Array.isArray(availableTargetMatches) ? availableTargetMatches : [],
    referenceMetricSources: referenceMetricSources || {},
    referenceScope: referenceScope || null,
    referenceMatchDate: referenceMatchDate || null,
    similarityHistory: Array.isArray(similarityHistory) ? similarityHistory : [],
    dataCollection: dataCollection || null,
    status: recapStatus || null,
    loadError: loadError || null,
    vmaMaxByDay: vmaMaxByDay && typeof vmaMaxByDay === "object" ? vmaMaxByDay : {},
    computedReferenceValues: computedReferenceValues || {},
    referenceOverrides: referenceOverrides || {},
  };
}

export async function fetchWeeklyRecapGlobalPlan({
  season,
  metrics = [...WEEKLY_RECAP_REFERENCE_METRICS, ...WEEKLY_RECAP_INTERNAL_METRICS],
  fallbackJDays = WEEKLY_RECAP_J_DAYS,
  clubId,
}) {
  if (isMaquetteClub(clubId)) {
    return buildMaquetteGlobalPlan({ season, metrics, fallbackJDays });
  }

  const query = new URLSearchParams();
  if (season) query.set("season", season);
  const endpoint = `/reports/weekly-recap/plan${query.toString() ? `?${query.toString()}` : ""}`;

  try {
    const response = await fetchAuth(endpoint, weeklyRecapFetchOptions());
    if (!response.ok) {
      throw new Error(`Failed to fetch weekly recap global plan: ${response.status}`);
    }

    const payload = await response.json();
    const jDays = Array.isArray(payload?.j_days) && payload.j_days.length > 0
      ? payload.j_days
      : fallbackJDays;

    const planPercentages = normalizePercentageMatrix(
      payload?.plan_percentages || payload?.planning_percentages || payload?.planification_percentages,
      metrics,
      jDays,
    );

    const microcycleRaw = payload?.effective_microcycle || payload?.microcycle;
    const microcycle = normalizeMicrocycleConfig(microcycleRaw);
    const calculateSimilarityFromDay =
      payload?.microcycle?.effective_calculate_similarity_from ||
      payload?.microcycle?.calculate_similarity_from ||
      microcycle.calculate_similarity_from;

    return {
      source: "api",
      endpoint,
      jDays,
      planPercentages,
      displayConfig: payload?.display_config || payload?.displayConfig || null,
      microcycle,
      calculateSimilarityFromDay,
      nextMatchDate: payload?.next_match_date || null,
      nextMatchDay: payload?.next_match_day || null,
      targetMatchDate: payload?.target_match_date || null,
      availableTargetMatches: Array.isArray(payload?.available_target_matches)
        ? payload.available_target_matches
        : [],
      raw: payload,
    };
  } catch (error) {
    return {
      source: "fallback",
      endpoint,
      jDays: fallbackJDays,
      planPercentages: createDefaultPercentageMatrix(metrics, fallbackJDays),
      displayConfig: null,
      microcycle: normalizeMicrocycleConfig(null),
      calculateSimilarityFromDay: "MD+1",
      nextMatchDate: null,
      nextMatchDay: null,
      targetMatchDate: null,
      availableTargetMatches: [],
      error: error?.message || "Unable to fetch weekly recap global plan",
    };
  }
}

export async function fetchPlayerWeeklyRecapBundle({
  player,
  season,
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
  jDays = WEEKLY_RECAP_J_DAYS,
  targetMatchDate,
  clubId,
  globalPlanPercentages,
}) {
  if (isMaquetteClub(clubId)) {
    return buildMaquettePlayerBundle({
      player,
      season,
      metrics,
      jDays,
      targetMatchDate,
      globalPlanPercentages,
    });
  }

  const playerId = player?.id;
  const encodedPlayerId = encodeId(playerId);
  const query = new URLSearchParams();
  if (season) query.set("season", season);
  if (targetMatchDate) query.set("target_match_date", targetMatchDate);

  const endpoint = `/players/${encodedPlayerId}/weekly-recap${query.toString() ? `?${query.toString()}` : ""}`;

  try {
    const response = await fetchAuth(endpoint, weeklyRecapFetchOptions());
    if (!response.ok) {
      throw new Error(`Failed to fetch weekly recap: ${response.status}`);
    }

    const payload = await response.json();
    const responseDays = Array.isArray(payload?.j_days) && payload.j_days.length > 0
      ? payload.j_days
      : Array.isArray(payload?.days) && payload.days.length > 0
        ? payload.days
        : jDays;

    const normalizedPlanPercentages = normalizePercentageMatrix(
      payload?.plan_percentages || payload?.planning_percentages || payload?.planification_percentages,
      metrics,
      responseDays,
    );

    const referenceValues = extractReferenceValues(
      player,
      payload?.reference_metrics || payload?.reference || payload?.averages,
      metrics,
    );
    let computedReferenceValues = extractComputedReferenceValues(
      payload?.computed_reference_metrics,
      metrics,
    );
    const referenceOverrides = extractOverrideMap(payload?.reference_overrides, metrics);
    if (!Object.values(computedReferenceValues).some((value) => value != null)) {
      computedReferenceValues = Object.fromEntries(
        metrics.map((metric) => {
          if (Object.prototype.hasOwnProperty.call(referenceOverrides, metric.key)) {
            return [metric.key, null];
          }
          return [metric.key, referenceValues?.[metric.key] ?? null];
        }),
      );
    }

    const actualValues = normalizeDayMetricMatrix(
      payload?.actual_metrics || payload?.actual || payload?.real_metrics,
      metrics,
      responseDays,
    );

    return buildBundle({
      player,
      metrics,
      jDays: responseDays,
      planPercentages: normalizedPlanPercentages,
      referenceValues,
      actualValues,
      displayConfig: payload?.display_config || payload?.displayConfig,
      microcycle: payload?.microcycle,
      calculateSimilarityFromDay:
        payload?.microcycle?.effective_calculate_similarity_from ||
        payload?.microcycle?.calculate_similarity_from,
      source: "api",
      endpoint,
      sourceDetails: "Loaded from weekly recap endpoints",
      similarityByMetricRaw: payload?.similarity_by_metric,
      weeklySimilarityRaw: payload?.weekly_similarity,
      externalWeeklySimilarityRaw: payload?.external_weekly_similarity,
      internalWeeklySimilarityRaw: payload?.internal_weekly_similarity,
      targetMatchDate: payload?.target_match_date,
      targetMatchOpponent: payload?.target_match_opponent,
      availableTargetMatches: payload?.available_target_matches,
      referenceMetricSources: payload?.reference_metric_sources || payload?.reference_source_by_metric,
      referenceScope: payload?.reference_scope || payload?.reference_source_kind,
      referenceMatchDate: payload?.reference_match_date,
      similarityHistory: payload?.similarity_history || payload?.weekly_similarity_history,
      dataCollection: payload?.data_collection || null,
      recapStatus: payload?.status || "ok",
      loadError: payload?.error || null,
      vmaMaxByDay: payload?.vma_max_by_day || payload?.vmaMaxByDay || {},
      computedReferenceValues,
      referenceOverrides,
    });
  } catch (error) {
    const fallbackPlanPercentages = createDefaultPercentageMatrix(metrics, jDays);
    const fallbackReferenceValues = extractReferenceValues(player, null, metrics);

    return buildBundle({
      player,
      metrics,
      jDays,
      planPercentages: fallbackPlanPercentages,
      referenceValues: fallbackReferenceValues,
      actualValues: null,
      displayConfig: null,
      microcycle: null,
      calculateSimilarityFromDay: null,
      source: "fallback",
      endpoint,
      sourceDetails: error?.message || "Unable to load weekly recap data",
      targetMatchDate: targetMatchDate || null,
      targetMatchOpponent: null,
      availableTargetMatches: [],
      referenceMetricSources: {},
      referenceScope: null,
      referenceMatchDate: null,
      similarityHistory: [],
      recapStatus: "load_failed",
      loadError: error?.message || "Unable to load weekly recap data",
      vmaMaxByDay: {},
      computedReferenceValues: fallbackReferenceValues,
      referenceOverrides: {},
    });
  }
}

export async function updatePlayerWeeklyRecapPlan({ playerId, season, planPercentages }) {
  const encodedPlayerId = encodeId(playerId);
  const endpoint = `/players/${encodedPlayerId}/weekly-recap/plan`;

  const body = {
    ...(season ? { season } : {}),
    plan_percentages: planPercentages,
  };

  const response = await fetchAuth(endpoint, weeklyRecapFetchOptions({
    method: "PATCH",
    body: JSON.stringify(body),
  }));

  if (!response.ok) {
    const details = await response.json().catch(() => ({}));
    throw new Error(details?.message || details?.error || `Failed to save planning percentages (${response.status})`);
  }

  return response.json().catch(() => ({ ok: true }));
}

export async function updatePlayerWeeklyRecapReferences({
  playerId,
  season,
  referenceMetrics,
  resetMetrics,
  resetAll = false,
}) {
  const encodedPlayerId = encodeId(playerId);
  const endpoint = `/players/${encodedPlayerId}/weekly-recap/references`;

  const body = {
    ...(season ? { season } : {}),
    ...(resetAll ? { reset_all: true } : {}),
    ...(Array.isArray(resetMetrics) && resetMetrics.length ? { reset_metrics: resetMetrics } : {}),
    ...(referenceMetrics && typeof referenceMetrics === "object"
      ? { reference_metrics: referenceMetrics }
      : {}),
  };

  const response = await fetchAuth(endpoint, weeklyRecapFetchOptions({
    method: "PATCH",
    body: JSON.stringify(body),
  }));

  if (!response.ok) {
    const details = await response.json().catch(() => ({}));
    throw new Error(details?.message || details?.error || `Failed to save reference values (${response.status})`);
  }

  return response.json().catch(() => ({ ok: true }));
}

export async function updateWeeklyRecapGlobalPlan({
  season,
  planPercentages,
  displayConfig,
  microcycle,
  calculateSimilarityFromDay,
  targetMatchDate,
}) {
  const endpoint = "/reports/weekly-recap/plan";

  const body = {
    ...(season ? { season } : {}),
    ...(planPercentages ? { plan_percentages: planPercentages } : {}),
    ...(displayConfig ? { display_config: displayConfig } : {}),
    ...(microcycle ? { microcycle } : {}),
    ...(calculateSimilarityFromDay ? { calculate_similarity_from: calculateSimilarityFromDay } : {}),
    ...(targetMatchDate !== undefined ? { target_match_date: targetMatchDate } : {}),
  };

  const response = await fetchAuth(endpoint, weeklyRecapFetchOptions({
    method: "PATCH",
    body: JSON.stringify(body),
  }));

  if (!response.ok) {
    const details = await response.json().catch(() => ({}));
    throw new Error(details?.message || details?.error || `Failed to save global planning percentages (${response.status})`);
  }

  return response.json().catch(() => ({ ok: true }));
}

export function rebuildBundleWithNewPlan(bundle, metrics = WEEKLY_RECAP_REFERENCE_METRICS) {
  const normalizedPlanPercentages = normalizePercentageMatrix(
    bundle?.planPercentages,
    metrics,
    bundle?.jDays || WEEKLY_RECAP_J_DAYS,
  );

  return buildBundle({
    player: { id: bundle?.playerId },
    metrics,
    jDays: bundle?.jDays || WEEKLY_RECAP_J_DAYS,
    planPercentages: normalizedPlanPercentages,
    referenceValues: bundle?.referenceValues || {},
    actualValues: bundle?.actualValues ?? null,
    displayConfig: bundle?.displayConfig || null,
    microcycle: bundle?.microcycle || null,
    calculateSimilarityFromDay: bundle?.calculateSimilarityFromDay || null,
    source: bundle?.source || "fallback",
    endpoint: bundle?.endpoint || null,
    sourceDetails: bundle?.sourceDetails || null,
    targetMatchDate: bundle?.targetMatchDate || null,
    targetMatchOpponent: bundle?.targetMatchOpponent || null,
    availableTargetMatches: bundle?.availableTargetMatches || [],
    referenceMetricSources: bundle?.referenceMetricSources || {},
    referenceScope: bundle?.referenceScope || null,
    referenceMatchDate: bundle?.referenceMatchDate || null,
    similarityHistory: bundle?.similarityHistory || [],
    dataCollection: bundle?.dataCollection || null,
    recapStatus: bundle?.status || null,
    loadError: bundle?.loadError || null,
    vmaMaxByDay: bundle?.vmaMaxByDay || {},
    computedReferenceValues: bundle?.computedReferenceValues || {},
    referenceOverrides: bundle?.referenceOverrides || {},
  });
}

export function mergeEffectiveReferenceValues({
  computedReferenceValues = {},
  referenceOverrides = {},
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
}) {
  return Object.fromEntries(
    (metrics || []).map((metric) => {
      const key = metric.key;
      if (Object.prototype.hasOwnProperty.call(referenceOverrides, key)) {
        return [key, referenceOverrides[key]];
      }
      return [key, computedReferenceValues?.[key] ?? null];
    }),
  );
}

export function rebuildBundleWithNewReferences(
  bundle,
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
  nextOverrides = {},
) {
  let computedReferenceValues = bundle?.computedReferenceValues || {};
  if (!Object.values(computedReferenceValues).some((value) => value != null)) {
    const previousOverrides = bundle?.referenceOverrides || {};
    computedReferenceValues = Object.fromEntries(
      (metrics || []).map((metric) => {
        const key = metric.key;
        if (Object.prototype.hasOwnProperty.call(previousOverrides, key)) {
          return [key, computedReferenceValues?.[key] ?? null];
        }
        return [key, bundle?.referenceValues?.[key] ?? null];
      }),
    );
  }

  const referenceValues = mergeEffectiveReferenceValues({
    computedReferenceValues,
    referenceOverrides: nextOverrides,
    metrics,
  });
  const nextSources = { ...(bundle?.referenceMetricSources || {}) };
  (metrics || []).forEach((metric) => {
    const previous = nextSources[metric.key] || {};
    if (Object.prototype.hasOwnProperty.call(nextOverrides || {}, metric.key)) {
      nextSources[metric.key] = {
        kind: "manual",
        match_date: previous.match_date || bundle?.referenceMatchDate || null,
        simulation_id: previous.simulation_id || null,
      };
    } else if (previous?.kind === "manual") {
      nextSources[metric.key] = {
        kind: "computed",
        match_date: previous.match_date || bundle?.referenceMatchDate || null,
      };
    }
  });

  return rebuildBundleWithNewPlan(
    {
      ...(bundle || {}),
      referenceValues,
      computedReferenceValues,
      referenceOverrides: nextOverrides || {},
      referenceMetricSources: nextSources,
      referenceScope: bundle?.referenceScope,
    },
    metrics,
  );
}

export function buildWeeklyRecapBundleCacheKey(playerId, season, targetMatchDate) {
  return `${playerId || "unknown"}::${season || ""}::${targetMatchDate || ""}`;
}

export function applyGlobalPlanToBundle(
  bundle,
  globalPlanPercentages,
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
) {
  return rebuildBundleWithNewPlan(
    {
      ...(bundle || {}),
      planPercentages: normalizePercentageMatrix(
        globalPlanPercentages,
        metrics,
        bundle?.jDays || WEEKLY_RECAP_J_DAYS,
      ),
    },
    metrics,
  );
}
